import { canWriteAgent, type AgentViewerAccess } from '@reflex/shared';
import type { Agent } from '@/lib/types';

export interface AgentAccess {
  /** What the caller may do with this session, as the server answered. */
  level: AgentViewerAccess | undefined;
  /**
   * Whether to offer controls that mutate the session. False only when the
   * server said `view`: an absent level means the payload had no caller to
   * answer for (a websocket broadcast, a fixture) and gating on it would
   * strip a user's own session of its controls.
   */
  canWrite: boolean;
  /** The caller can read the run but not act on it. */
  isReadOnly: boolean;
}

/**
 * What the signed-in user may do with one agent session.
 *
 * The server is the enforcement point and always has been — every agent
 * route runs `verifyOwnership`. This is purely about not offering controls
 * that are guaranteed to 403: before it, a `view`-only grantee got the full
 * interactive session, typed a message, and got a generic error toast.
 *
 * Read it from the agent payload rather than from `useAuth().can(...)`:
 * access to another user's run comes from a per-resource grant, which no
 * org-wide permission slug can answer for.
 */
export function useAgentAccess(agent: Agent | undefined): AgentAccess {
  const level = agent?.viewerAccess;
  const canWrite = canWriteAgent(level);
  return { level, canWrite, isReadOnly: !canWrite };
}
